import { CUSTOM_FIELD_KEYS, PageTypeEnum } from '@/constant';
import {
	GetWorkerUnauthorizedDepartureDetailResponse,
	GetWorkerUnauthorizedDepartureListResponse,
	IApplicantListItem,
	IFranchiseListItem,
} from '@/services';

import {
	convertVisaCodeToName,
	formatCustomFieldValueToExcel,
	formatDate,
	getDiffExpirationDateString,
	insertObjectIfV2,
	map,
} from 'utils';

import { isNotEmpty } from 'ramda';

import { formatWorkerApplicantListItem } from '../worker-applicant';
import { formatWorkerFranchiseListItem } from '../worker-franchise';

export const formatWorkerUnauthorizedDepartureDetail = (
	input: GetWorkerUnauthorizedDepartureDetailResponse['content'][number]
) => {
	return {
		...input,
		applicantVisaCode: convertVisaCodeToName(input?.applicantVisaCode),
		registDt: formatDate(input.registDt),
		updateDt: formatDate(input.updateDt),
		applicantBirthDate: formatDate(input?.applicantBirthDate),
	};
};

const filterByPrefixKeyWithFormat = <T extends object>(
	obj: T,
	prefix: PageTypeEnum
) => {
	const result = {} as T;
	for (const key in obj) {
		if (key.startsWith(prefix?.toLocaleLowerCase())) {
			result[key] = obj[key];
		}
	}

	return result;
};

export const formatWorkerUnauthorizedDepartureListItem = (
	input: GetWorkerUnauthorizedDepartureListResponse['content'][number]
) => {
	let _input = input;

	let newFranchiseFormat = filterByPrefixKeyWithFormat<IFranchiseListItem>(
		_input,
		PageTypeEnum.FRANCHISE
	);
	if (isNotEmpty(newFranchiseFormat)) {
		newFranchiseFormat = formatWorkerFranchiseListItem(newFranchiseFormat);
	}

	let newApplicantFormat = filterByPrefixKeyWithFormat<IApplicantListItem>(
		_input,
		PageTypeEnum.APPLICANT
	);
	if (isNotEmpty(newApplicantFormat)) {
		newApplicantFormat = formatWorkerApplicantListItem(newApplicantFormat);
	}

	const formatValues = {
		...newFranchiseFormat,
		...newApplicantFormat,
	} as typeof _input;

	return {
		...input,
		...insertObjectIfV2(isNotEmpty(formatValues), {
			...formatValues,
		}),
		id: input?.unauthorizedDepartureId,
		unauthorizedDepartureRegistDt: formatDate(
			input?.unauthorizedDepartureRegistDt
		),
		unauthorizedDepartureUpdateDt: formatDate(
			input?.unauthorizedDepartureUpdateDt
		),
		unauthorizedDepartureOccuringDate: formatDate(
			input?.unauthorizedDepartureOccuringDate
		),
	};
};

export const formatWorkerUnauthorizedDepartureList = (
	input: GetWorkerUnauthorizedDepartureListResponse['content']
) => {
	return map(input, formatWorkerUnauthorizedDepartureListItem);
};

export const formatWorkerUnauthorizedDepartureExcelList = (
	input: GetWorkerUnauthorizedDepartureListResponse['content']
) => {
	return map(input, (item) => {
		return {
			...formatWorkerUnauthorizedDepartureListItem(item),
			applicantDepartureDate: getDiffExpirationDateString(
				item?.applicantDepartureDate ?? ''
			),
			applicantExpirationDate: getDiffExpirationDateString(
				item?.applicantExpirationDate ?? ''
			),
			franchiseProductionScale: item?.franchiseProductionScale
				? Number(item?.franchiseProductionScale)?.toLocaleString?.('ko-KR')
				: '',
			...formatCustomFieldValueToExcel(
				[...CUSTOM_FIELD_KEYS.applicant, ...CUSTOM_FIELD_KEYS.franchise].reduce(
					(acc, key) => ({
						...acc,
						[key]: item?.[key as keyof typeof item],
					}),
					{}
				)
			),
		};
	});
};
